/**
 * The integration registry — one source of truth for every external service
 * this product uses, could use, or deliberately will never automate.
 *
 * WHY THIS FILE EXISTS
 *
 * API knowledge kept leaking into three places that disagreed: pipeline YAML,
 * scattered client modules, and people's memory. A key was provisioned for
 * Google Civic and no client was ever written; `OPENSECRETS_API_KEY` is
 * referenced by the ingestion pipeline and defined nowhere. Neither was
 * visible from anywhere you would look.
 *
 * TWO CONSUMERS, ONE ARRAY
 *
 *   1. Admin → Integrations, so the gaps are visible to whoever is planning.
 *   2. The district onboarding help panel, which shows the `userFacing` rows.
 *
 * Rendering the same rows twice from two hardcoded lists is how the two
 * eventually disagree. Adding an integration is one object here.
 *
 * STATUS IS A CLAIM ABOUT EVIDENCE, NOT AN INTENTION
 *
 *   live         a client exists in this repo and runs. `evidence` points at it.
 *   provisioned  configuration exists but no working client does. This is debt,
 *                and the `gap` field says exactly what is missing.
 *   future       a real candidate for integration, not yet started.
 *   linkOnly     no public API exists and none is expected. These are permanent
 *                handoffs to a human, NOT a backlog. Filing them as "future"
 *                would put items on a list that can never be completed.
 *
 * Do not mark something `live` because a key exists. Google Civic is the
 * cautionary tale: a key in two places and nothing that calls it.
 */

export const STATUS = {
  retired: { label: 'Retired', color: '#64748B', bg: '#F1F5F9' },
  unverified: { label: 'Access unverified', color: '#8A6410', bg: '#FBF1DC' },
  live: { label: 'Live', color: '#166534', bg: '#DCFCE7' },
  provisioned: { label: 'Key, no client', color: '#8A6410', bg: '#FBF1DC' },
  future: { label: 'Future', color: '#1E40AF', bg: '#DBEAFE' },
  linkOnly: { label: 'Link only — no API', color: '#64748B', bg: '#F1F5F9' },
}

export const INTEGRATIONS = [
  { id: 'openfec-finance', name: 'OpenFEC financial totals', url: 'https://api.open.fec.gov/developers/', status: 'unverified', auth: 'Server-only OPENFEC_API_KEY', purpose: 'Candidate and committee receipts, disbursements and reporting periods, linked by FEC ID.', evidence: 'src/engine/federalFinance.js; scripts/fetchFederalFinance.mjs', gap: 'Contract-tested adapter. A credentialed live snapshot and approved storage target are required before enabling the finance surface.' },
  { id: 'virginia-disclosure', name: 'Virginia official campaign disclosures', url: 'https://cfreports.elections.virginia.gov/', status: 'linkOnly', auth: 'Public portal', purpose: 'Official state campaign-finance reports.', gap: 'Verified public portal. Automated feed schema, licensing and update cadence must be confirmed before ingestion.' },
  { id: 'maplight', name: 'MapLight', url: 'https://www.maplight.org', status: 'retired', auth: 'No verified public feed', purpose: 'Historical campaign finance reference.', gap: 'Not an active integration. Use OpenFEC for federal finance and verified official state disclosure feeds for state finance.' },
  // ── Live ─────────────────────────────────────────────────────────────────
  {
    id: 'census-geocoder',
    name: 'US Census Geocoder',
    url: 'https://geocoding.geo.census.gov',
    status: 'live',
    auth: 'Keyless',
    purpose: 'Address → congressional and state legislative districts. Powers district lookup.',
    evidence: 'src/engine/districtLookup.js; proxied same-origin since AB#701',
  },
  {
    id: 'congress-legislators',
    name: 'congress-legislators',
    url: 'https://unitedstates.github.io/congress-legislators/',
    status: 'live',
    auth: 'Keyless',
    purpose: 'Legislator identity by Bioguide ID, never by name.',
    evidence: 'src/engine/congressLegislatorsClient.js (AB#495)',
  },
  {
    id: 'congress-gov',
    name: 'Congress.gov API',
    url: 'https://api.congress.gov',
    status: 'live',
    auth: 'CONGRESS_API_KEY',
    purpose: 'Federal bills and sponsorship, feeding stance classification.',
    evidence: 'src/engine/congressClient.js',
  },
  {
    id: 'openfec',
    name: 'OpenFEC',
    url: 'https://api.open.fec.gov',
    status: 'live',
    auth: 'FEC_API_KEY / OPENFEC_API_KEY',
    purpose: 'Federal candidate listings. Finance retrieval is a separate server-side client; live finance coverage is not yet attested.',
    evidence: 'src/engine/openFecClient.js',
  },
  {
    id: 'openstates',
    name: 'OpenStates',
    url: 'https://v3.openstates.org',
    status: 'live',
    auth: 'OPENSTATES_API_KEY',
    purpose: 'State legislators, bills and votes.',
    evidence: 'src/engine/openStatesClient.js',
  },
  {
    id: 'supabase',
    name: 'Supabase',
    url: 'https://supabase.com',
    status: 'live',
    auth: 'anon key (RLS-scoped) + service role',
    purpose: 'Database, auth and row-level security.',
    evidence: 'src/lib/supabase.js',
  },

  // ── Provisioned but not built ────────────────────────────────────────────
  {
    id: 'google-civic',
    name: 'Google Civic Information',
    url: 'https://developers.google.com/civic-information',
    status: 'provisioned',
    auth: 'VITE_GOOGLE_CIVIC_KEY, STAGING_CONFIG_CIVIC_KEY',
    purpose: 'Was intended for representatives and election lookup by address.',
    gap: 'A key is configured in two places and NO client module exists. Before building one, verify the representatives endpoint is still operating — it was slated for retirement and may no longer serve this data. Do not assume.',
  },
  {
    id: 'opensecrets',
    name: 'OpenSecrets',
    url: 'https://www.opensecrets.org/open-data/api',
    status: 'unverified',
    auth: 'OPENSECRETS_API_KEY',
    purpose: 'Donor and influence data behind the influence map.',
    gap: 'Referenced at azure-pipelines-ingestion.yml:117 and defined nowhere. Azure leaves an undefined $(NAME) as a literal string rather than failing, so the influence path silently receives the unexpanded text instead of a key.',
    evidence: 'src/engine/openSecretsClient.js',
  },

  // ── Future ───────────────────────────────────────────────────────────────
  {
    id: 'ballotpedia',
    name: 'Ballotpedia',
    url: 'https://ballotpedia.org',
    status: 'future',
    auth: 'Paid API',
    purpose:
      'The most complete single source: congressional district, state legislative districts, and often local offices such as school board and county positions, with current officeholders.',
    gap: 'Commercial licence required. The realistic route to full-ballot coverage below federal level.',
    userFacing: true,
    userNote: 'Best overall starting point. Shows congressional, state legislative and often local offices, with who holds each seat.',
    userOrder: 1,
  },
  {
    id: 'census-tigerweb',
    name: 'Census TIGERweb',
    url: 'https://tigerweb.geo.census.gov',
    status: 'future',
    auth: 'Keyless',
    purpose: 'Authoritative district boundary geometry and GEOIDs.',
    gap: 'Strongly recommended: this is the structural fix for AB#699. District identity would come from boundary geometry rather than being parsed out of a prose label, which is why MA, NH, VT, AK and MD can never currently receive a district key.',
  },
  {
    id: 'geocodio',
    name: 'Geocodio',
    url: 'https://www.geocod.io',
    status: 'future',
    auth: 'Paid API key',
    purpose: 'Returns congressional and state legislative districts in a single call.',
    gap: 'Candidate fallback for addresses the Census geocoder cannot resolve, which is the most common failure in district onboarding.',
  },
  {
    id: 'votesmart',
    name: 'Vote Smart',
    url: 'https://votesmart.org/share/api',
    status: 'future',
    auth: 'API key on request',
    purpose: 'Candidate issue positions from the Political Courage Test.',
    gap: 'Directly addresses the thinnest input in CAM: candidate positions. Today most candidates are unscorable for want of them.',
  },
  {
    id: 'usps-address',
    name: 'USPS Address Validation',
    url: 'https://www.usps.com/business/web-tools-apis/',
    status: 'future',
    auth: 'Free, registration required',
    purpose: 'Normalise and verify an address before geocoding it.',
    gap: 'Reduces district lookup failures caused by malformed input rather than by missing coverage.',
  },
  {
    id: 'govtrack',
    name: 'GovTrack',
    url: 'https://www.govtrack.us/developers',
    status: 'future',
    auth: 'Keyless bulk data',
    purpose: 'Legislator and voting records.',
    gap: 'Useful independent cross-check against congress-legislators rather than a primary source.',
  },
  {
    id: 'propublica-congress',
    name: 'ProPublica Congress API',
    url: 'https://projects.propublica.org/api-docs/congress-api/',
    status: 'retired',
    auth: 'Unavailable',
    purpose: 'Historical congressional data only.',
    gap: 'Official documentation confirms permanent unavailability. Use Congress.gov for supported federal legislative records.',
  },

  // ── Link only: no public API exists, and none is expected ────────────────
  {
    id: 'vote411',
    name: 'VOTE411',
    url: 'https://www.vote411.org',
    status: 'linkOnly',
    auth: 'None — public site',
    purpose:
      'League of Women Voters. Covers every state and shows districts as part of a personalised ballot lookup.',
    gap: 'No public API. What is worth copying here is the COVERAGE — every race on the ballot, not just federal — which is a product goal, not an integration task. Tracked as full-ballot coverage, not as "integrate VOTE411".',
    userFacing: true,
    userNote: 'Covers every state and shows your full personalised ballot. Especially useful close to an election.',
    userOrder: 2,
  },
  {
    id: 'house-gov',
    name: 'House.gov — Find Your Representative',
    url: 'https://www.house.gov/representatives/find-your-representative',
    status: 'linkOnly',
    auth: 'None — public site',
    purpose: 'Official source for a US House district.',
    gap: 'A web form, not an API. US House district only.',
    userFacing: true,
    userNote: 'Official source, but only for your U.S. House district.',
    userOrder: 3,
  },
  {
    id: 'canivote',
    name: 'CanIVote.org',
    url: 'https://www.canivote.org',
    status: 'linkOnly',
    auth: 'None — public site',
    purpose:
      'National Association of Secretaries of State. Directs to each state official voter lookup portal.',
    gap: 'A directory of fifty state portals, each different. No single API. The state portals are nevertheless the authoritative record.',
    userFacing: true,
    userNote:
      'Links to your state official portal — the authoritative record of the districts tied to your registration. Use this if your address is near a boundary or the lines were recently redrawn.',
    userOrder: 4,
  },
]

/** The rows the district help panel offers a voter, in the intended order. */
export const USER_FACING_LOOKUPS = INTEGRATIONS
  .filter(i => i.userFacing)
  .sort((a, b) => (a.userOrder ?? 99) - (b.userOrder ?? 99))

/** Counts for the Admin summary. Derived, never hand-maintained. */
export const countByStatus = () =>
  INTEGRATIONS.reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {})
